import { create } from 'zustand';
import { createTheme, Theme } from '@mui/material/styles';
// Types the MuiDataGrid entry in getMuiTheme's `mixins`
import type {} from '@mui/x-data-grid/themeAugmentation';
import { ApiService } from '@/shared/services/api.service';
import { useGuestStore } from './guest.store';

// Predefined theme configurations
const themes: Record<string, Record<string, any>> = {
  royalBlue: {
    mode: 'light',
    primary: '#0d3e6e',
    secondary: '#dc004e',
    opacity8: 'rgba(13, 62, 110, 0.8)',
    opacity1: 'rgba(13, 62, 110, 0.1)',
    opacity6: 'rgba(13, 62, 110, 0.5)',
    background: { default: '#dde4eb', paper: '#FFF' },
    text: { primary: '#0d3e6e', secondary: '#0d3e6eb0', disabled: '#757575' },
    sidebar: {
      text: '#0d3e6e',
      background: '#FFF',
      active: {
        background: 'rgba(13, 62, 110, 0.9)',
        text: '#FFF',
      },
    },
    boxShadow: '0 4px 6px 2px rgb(13 62 110 / 26%)',
    borderRadius: '8px',
    borderColor: '#e0e0e0',
  },
  light: {
    mode: 'light',
    primary: '#3c85b0',
    secondary: '#dc004e',
    opacity8: 'rgba(60, 133, 176, 0.8)',
    opacity1: 'rgba(60, 133, 176, 0.1)',
    opacity6: 'rgba(60, 133, 176, 0.5)',
    background: { default: '#dde4eb', paper: '#FFF' },
    text: { primary: '#0e5179', secondary: '#0e5179b0', disabled: '#757575' },
    sidebar: {
      text: '#3c85b0',
      background: '#FFF',
      active: {
        background: 'rgba(60, 133, 176, 0.9)',
        text: '#FFF',
      },
    },
    boxShadow: '0 4px 6px 2px rgb(13 62 110 / 26%)',
    borderRadius: '8px',
    borderColor: '#e0e0e0',
  },
  dark: {
    mode: 'dark', // Fix the mode here
    primary: '#151b23',
    secondary: '#66bb6a',
    opacity8: 'rgba(21, 27, 35, 0.8)',
    opacity1: 'rgba(21, 27, 35, 0.1)',
    opacity6: 'rgba(21, 27, 35, 0.5)',
    background: { default: '#151b23', paper: '#151b23' }, // Darker shades
    text: { primary: '#ffffff', secondary: '#bdbdbd', disabled: '#757575' },
    sidebar: { text: '#ffffff', background: '#333', active: { background: '#555', text: '#FFF' } },
    boxShadow: '0 4px 6px 2px rgb(21 27 35 / 26%)',
    borderRadius: '8px',
    borderColor: '#e0e0e0',
  },
  gray: {
    mode: 'light',
    primary: '#0000007a',
    secondary: '#616161',
    opacity8: '#0000007a',
    opacity1: 'rgb(0, 0, 0, 0.1)',
    opacity6: 'rgb(0, 0, 0, 0.5)',
    background: { default: '#dde4eb', paper: '#FFF' },
    text: { primary: '#000000d4', secondary: '#00000094', disabled: '#757575' },
    sidebar: {
      text: '#FFF',
      background: '#4c4b4be6',
      active: {
        background: '#7d7a7a',
        text: '#FFF',
      },
    },
    boxShadow: '0 4px 6px 2px rgb(0 0 0 / 26%)',
    borderRadius: '8px',
    borderColor: '#e0e0e0',
  },
  green: {
    mode: 'light',
    primary: 'rgba(13, 124, 60, 0.8)',
    secondary: '#81c784',
    opacity8: 'rgba(13, 124, 60, 0.8)',
    opacity1: 'rgba(13, 124, 60, 0.1)',
    opacity6: 'rgba(13, 124, 60, 0.5)',
    background: { default: '#0d7c3c2b', paper: '#FFF' },
    text: { primary: '#0a4d28', secondary: 'rgb(10 77 40 / 65%)', disabled: '#757575' },
    sidebar: {
      text: '#0d7c3c',
      background: '#FFF',
      active: {
        background: 'rgba(13, 124, 60, 0.9)',
        text: '#FFF',
      },
    },
    boxShadow: '0 4px 6px 2px rgb(13 124 60 / 26%)',
    borderRadius: '8px',
    borderColor: '#e0e0e0',
  },
};

// Retrieve theme from localStorage or use default theme
const storedTheme = localStorage.getItem('appTheme') || 'green';

// Define theme store
interface ThemeStore {
  themeName: string;
  themeColors: Record<any, any>;
  currentSettings: any;
  setTheme: (themeName: string) => void;
  setCurrentSettings: (settings: any) => void;
  loadAppTheme: () => void;
}

const getBackgroundImage = async (): Promise<string> => {
  try {
    // Skip API call for guests
    const guestStore = useGuestStore.getState();
    const isGuest = !!(guestStore.guest && guestStore.guest.pid);
    const hasGuestToken = !!localStorage.getItem('isUserGuest');
    const isGuestRoute =
      window.location.pathname.includes('/welcome') ||
      window.location.pathname.includes('/guest-workplace') ||
      window.location.pathname.includes('/guest-lobby');

    if (isGuest || hasGuestToken || isGuestRoute) {
      console.log('[ThemeStore] ⏭️ Skipping theme fetch for guest user or guest route');
      return 'green'; // Default theme for guests
    }
    return useThemeStore.getState().themeName ?? 'green';
  } catch (error) {
    console.error('Failed to load background:', error);
    return 'green';
  }
};

export const useThemeStore = create<ThemeStore>((set) => ({
  currentSettings: {},
  themeName: storedTheme, // Default theme
  themeColors: themes[storedTheme] || 'green', // Set default colors
  setTheme: (themeName) => {
    if (themes[themeName]) {
      localStorage.setItem('appTheme', themeName); // Save to localStorage
      set({ themeName, themeColors: themes[themeName] }); // Updates all colors when theme changes
    }
  },
  setCurrentSettings: (settings: any) => {
    set({ currentSettings: settings });
  },
  loadAppTheme: async () => {
    const themeName = await getBackgroundImage();
    useThemeStore.getState().setTheme(themeName);
  },
}));

// Function to create a Material-UI theme dynamically
export const getMuiTheme = (themeColors: Record<string, any>): Theme =>
  createTheme({
    palette: {
      mode: themeColors.mode as 'light' | 'dark',
      primary: { main: themeColors.primary },
      secondary: { main: themeColors.secondary },
      error: { main: '#d32f2f' },
      background: { ...themeColors.background },
      text: { ...themeColors.text },
    },
    typography: {
      fontFamily: '"Montserrat", sans-serif',
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: themeColors.borderRadius || '8px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: themeColors.borderRadius || '8px',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: themeColors.text.primary,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              // borderColor: themeColors.borderColor,
            },
          },
        },
      },
    },
    mixins: {
      // DataGrid paints its sticky column headers (and pinned columns) with background.default,
      // which the green theme makes translucent ('#0d7c3c2b') — scrolled rows then show through
      // the header. The paper colour is opaque in every theme, so headers stay solid everywhere.
      MuiDataGrid: { containerBackground: themeColors.background.paper },
    },

    // shadows: [
    //   'none', // Default MUI shadow at index 0
    //   themeColors.boxShadow, // Custom shadow at index 1
    //   ...Array(23).fill('none'), // Ensure exactly 25 elements
    // ] as unknown as any,
  });
